import { slugify } from './slugify';

export const SITE_ORIGIN = 'https://symuliga.pl';

export const isEntityId = (id) => /^[1-9]\d{0,9}$/.test(String(id)) && Number(id) <= 2147483647;
const cleanGroup = (value) => {
  const text = String(value || '').trim();
  return ['brak', 'null', 'undefined'].includes(text.toLowerCase()) ? '' : text;
};

const entityPath = (kind, id, name, reserved) => {
  if (!isEntityId(id)) return null;
  const value = String(name || '').trim();
  let slug = ['null', 'undefined'].includes(value.toLowerCase()) ? '' : slugify(value.replace(/_/g, ' '));
  if (reserved.some(tool => tool.toLowerCase() === slug)) slug += kind === 'team' ? '-klub' : '-liga';
  return `/${kind}/${id}${slug ? `/${slug}` : ''}`;
};
export const teamPath = (id, name) => entityPath('team', id, name, ['analytics']);
export const leaguePath = (id, competition) => {
  const name = typeof competition === 'string' ? competition : competition
    ? competition.name ? [competition.name, cleanGroup(competition.group)].filter(Boolean).join(' ') : competition.full_name
    : '';
  return entityPath('liga', id, name, LEAGUE_TOOLS);
};

// These are real routes, never name slugs. Keep in sync with App.js.
export const LEAGUE_TOOLS = [
  'conditions', 'positionMatrix', 'playoff', 'team-comparisons', 'summary', 'fixtures',
  'points-threshold', 'standingsCalculator', 'fixture_difficulty', 'leagueTables',
  'live', 'yourSimulation', 'elo-ranking', 'wcPositionMatrix', 'wcYourSimulation',
  'wcSummary', 'wcPointThreshold', 'WcStandingsCalculator', 'wcMatchesProbabilities',
];

export const normalizeEntityPath = (pathname) => {
  const match = pathname.match(/^\/(team|liga)\/([1-9]\d*)(?:\/([^/]+))?\/?$/i);
  if (!match || !isEntityId(match[2])) return pathname;
  const [, kind, id, suffix] = match;
  const tools = kind.toLowerCase() === 'team' ? ['analytics'] : LEAGUE_TOOLS;
  if (suffix && tools.some(tool => tool.toLowerCase() === suffix.toLowerCase())) return pathname;
  // The authoritative name is known only after fetching this entity's data.
  // Never strip its slug here; pages replace aliases with their named canonical.
  return `/${kind.toLowerCase()}/${id}${suffix ? `/${suffix}` : ''}`;
};

// Normalize link destinations, retaining tool parameters, anchors and router state.
export const normalizeEntityLink = (to) => {
  if (typeof to === 'string') {
    const match = to.match(/^([^?#]*)(.*)$/);
    return normalizeEntityPath(match[1]) + match[2];
  }
  if (to && typeof to.pathname === 'string') return { ...to, pathname: normalizeEntityPath(to.pathname) };
  return to;
};

export const competitionName = (competition = {}) => {
  const group = String(competition.group || '').trim();
  const hasGroup = group && !['brak', 'null'].includes(group.toLowerCase());
  return competition.full_name || `${competition.name || 'Liga'}${hasGroup ? ` (${group})` : ''}`;
};

export const seasonLabel = (season) => season ? ` — sezon ${season}` : '';

export const getArchiveSeasons = (competitions, activeSeason) => !activeSeason ? [] : (
  [...new Set(Object.values(competitions || {}).flat()
    .filter(item => item.country === 'Poland' && item.type === 'league')
    .map(item => item.season)
    .filter(season => typeof season === 'string' && season.localeCompare(activeSeason) < 0))]
    .sort((a, b) => b.localeCompare(a))
);

const normalized = (value) => String(value ?? '').trim().toLowerCase();
const groupKey = (value) => ['brak', 'null'].includes(normalized(value)) ? '' : normalized(value);

export const findCurrentCompetition = (competition, competitions, activeSeason) => {
  if (!competition || !activeSeason || competition.season === activeSeason) return null;
  // Fail closed on ambiguity: do not send a regional visitor to a similarly named league.
  const candidates = Object.values(competitions || {}).flat().filter(candidate =>
    candidate.season === activeSeason && normalized(candidate.name) === normalized(competition.name)
    && groupKey(candidate.group) === groupKey(competition.group)
    && ['country', 'type', 'level', 'region_id'].every(key => normalized(candidate[key]) === normalized(competition[key]))
  );
  return candidates.length === 1 ? candidates[0] : null;
};
